// The project's call to action once the negotiation started: the asks, each
// waiting on a person or on InfinityLoop until answered, and the submission
// for analysis once all are fulfilled. The demoer answers an ask in place,
// as the person it waits on, with the same chat box the intake uses.

import { FC, useRef, useState } from "react";
import { CalendarOutlined, CheckOutlined, DownOutlined, HourglassFilled } from "@ant-design/icons";
import { clsx } from "clsx";
import { Button, Tooltip } from "../../../../design-system";
import { asksOf, isOpen } from "../model/derived";
import { DEMO_TODAY } from "../model/fixtures";
import { longDate, shortDate } from "../model/format";
import { UsageDemoState, answer, reroute, submit } from "../model/store";
import type { Ask, Fulfilment, UsageProject } from "../model/types";
import { ActionCard } from "./ActionCard";
import { ChatBox, PersonPicker, said, useClickOutside } from "./AskChat";
import classes from "./AsksCard.module.less";

const waitingOn = (ask: Ask): string => (ask.how.mode === "delegate" ? ask.how.to : "InfinityLoop");

const dueOf = (ask: Ask): string => (ask.how.mode === "delegate" ? ask.how.due : ask.need.due);

/** The owner, as a field: a click opens the directory and the pick reassigns the ask. */
const OwnerField: FC<{ ask: Ask; onPick: (to: string) => void }> = ({ ask, onPick }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);
    useClickOutside(ref, () => setOpen(false));
    return (
        <span className={classes.anchor} ref={ref}>
            <button type="button" className={clsx(classes.value, open && classes.valueOpen)} onClick={() => setOpen((o) => !o)}>
                {waitingOn(ask)}
                <DownOutlined className={classes.valueIcon} />
            </button>
            {open && (
                <div className={classes.pop}>
                    <PersonPicker
                        value={waitingOn(ask)}
                        onPick={(to) => {
                            setOpen(false);
                            onPick(to);
                        }}
                    />
                </div>
            )}
        </span>
    );
};

/** The due date, as a field: a click opens the browser's calendar on a date input hidden under it. */
const DueField: FC<{ ask: Ask; onPick: (due: string) => void }> = ({ ask, onPick }) => {
    const input = useRef<HTMLInputElement>(null);
    const openCalendar = () => {
        const el = input.current;
        if (!el) return;
        if ("showPicker" in el) (el as HTMLInputElement & { showPicker: () => void }).showPicker();
        else el.focus();
    };
    return (
        <span className={classes.anchor}>
            <button type="button" className={classes.value} onClick={openCalendar}>
                <CalendarOutlined className={classes.valueIcon} />
                {shortDate(dueOf(ask))}
            </button>
            <input
                ref={input}
                className={classes.hiddenDate}
                type="date"
                tabIndex={-1}
                aria-hidden
                value={dueOf(ask)}
                onChange={(e) => e.target.value && onPick(e.target.value)}
            />
        </span>
    );
};

/** What an answered ask settled on, with its date. */
const resultLine = (ask: Ask): string | undefined =>
    ask.answer ? `${said(ask.need, { mode: "answer", value: ask.answer.value })} · ${longDate(ask.answer.on)}` : undefined;

export const AsksCard: FC<{ project: UsageProject; state: UsageDemoState }> = ({ project, state }) => {
    const [answering, setAnswering] = useState<string | null>(null);
    // Only what waits on someone in the business shows; what InfinityLoop pulls is its own job.
    const asks = asksOf(project, state).filter((a) => a.how.mode === "delegate");
    const open = asks.filter(isOpen);
    const people = Array.from(new Set(open.map(waitingOn)));

    const title =
        open.length === 0
            ? "Submit the project for analysis"
            : `${open.length === 1 ? "One ask" : `${open.length} asks`} waiting on ${people.join(", ")}`;

    // The demoer answers for the person the ask waits on; a new route is recorded instead.
    const commit = (ask: Ask, how: Fulfilment) => {
        setAnswering(null);
        if (how.mode === "answer") answer(ask.need.id, { value: how.value, comment: "", on: DEMO_TODAY });
        else if (how.mode === "waive") answer(ask.need.id, { value: ask.need.waived, comment: "", on: DEMO_TODAY });
        else reroute(project.id, ask.need.id, how);
    };

    return (
        <ActionCard
            eyebrow="Asks"
            title={title}
            actions={
                <>
                    <Tooltip title={open.length > 0 ? `${open.length === 1 ? "One ask" : `${open.length} asks`} still open` : undefined} mouseEnterDelay={0.3}>
                        <Button
                            size="small"
                            type={open.length > 0 ? "default" : "primary"}
                            ghost={open.length > 0}
                            disabled={open.length > 0}
                            className={classes.submit}
                            onClick={() => submit(project.id, DEMO_TODAY)}
                        >
                            Submit for analysis
                        </Button>
                    </Tooltip>
                </>
            }
        >
            <div className={classes.list}>
                {asks.map((ask) => {
                    const done = !isOpen(ask);
                    const active = answering === ask.need.id;
                    return (
                        <div key={ask.need.id} className={classes.row}>
                            <div className={classes.rowHead}>
                                <span className={clsx(classes.icon, done && classes.iconDone)}>
                                    {done ? <CheckOutlined /> : <HourglassFilled />}
                                </span>
                                <div className={classes.text}>
                                    <div className={classes.title}>{ask.need.title}</div>
                                    {done && <div className={classes.state}>{resultLine(ask)}</div>}
                                </div>
                                {done ? (
                                    <>
                                        <span className={classes.valueDone}>{waitingOn(ask)}</span>
                                        <span className={classes.valueDone}>
                                            <CalendarOutlined className={classes.valueIcon} />
                                            {shortDate(dueOf(ask))}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <OwnerField ask={ask} onPick={(to) => reroute(project.id, ask.need.id, { mode: "delegate", to, due: dueOf(ask) })} />
                                        <DueField ask={ask} onPick={(due) => reroute(project.id, ask.need.id, { mode: "delegate", to: waitingOn(ask), due })} />
                                    </>
                                )}
                                {!done && (
                                    <Button
                                        type={active ? "default" : "primary"}
                                        ghost={active}
                                        size="small"
                                        className={classes.answerBtn}
                                        onClick={() => setAnswering(active ? null : ask.need.id)}
                                    >
                                        {active ? "Cancel" : "Answer"}
                                    </Button>
                                )}
                            </div>
                            {active && (
                                <div className={classes.chat}>
                                    <div className={classes.asWho}>Answering as {waitingOn(ask)}; in the product, from the email they got.</div>
                                    <ChatBox need={ask.need} onCommit={(how) => commit(ask, how)} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ActionCard>
    );
};
